import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import {
  getGetStoreSocialNetworksQueryKey,
  useCreateSocialNetwork,
  useDeleteSocialNetwork,
  useGetStoreSocialNetworks,
  useUpdateSocialNetwork,
} from "@/api";
import { CreateSocialNetworkDtoPlatform } from "@/api/schemas/createSocialNetworkDtoPlatform";
import { useSavedFlash } from "@/hooks/settings/useSavedFlash";
import { getApiErrorMessage } from "@/lib/api/errors";
import { SOCIAL_PLATFORM_DEFS, type SocialPlatformKey } from "@/lib/settings/constants";

type SocialEdits = {
  storeId: number | null;
  values: Partial<Record<SocialPlatformKey, string>>;
  primary: SocialPlatformKey | "" | null;
};

const EMPTY_EDITS: SocialEdits = { storeId: null, values: {}, primary: null };

/** "@shop", "shop" or a pasted profile URL all become the bare handle the backend builds links from. */
function normalizeHandle(value: string) {
  return value
    .trim()
    .replace(/^https?:\/\/[^/]+\//i, "")
    .replace(/^@/, "")
    .replace(/\/+$/, "");
}

/** Socials tab: one handle per platform plus the primary channel shown to customers. */
export function useSettingsSocials(storeId: number | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const { saved, flash } = useSavedFlash();
  const [edits, setEdits] = useState<SocialEdits>(EMPTY_EDITS);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useGetStoreSocialNetworks(storeId ?? 0, {
    query: { enabled: enabled && storeId !== null },
  });
  const networks = useMemo(() => (data?.status === 200 ? data.data.data : []), [data]);

  // Failures are collected into saveError below, so the global toast stays quiet.
  const silent = { mutation: { meta: { silentError: true } } };
  const { mutateAsync: createSocial } = useCreateSocialNetwork(silent);
  const { mutateAsync: updateSocial } = useUpdateSocialNetwork(silent);
  const { mutateAsync: deleteSocial } = useDeleteSocialNetwork(silent);

  const hasEdits = storeId !== null && edits.storeId === storeId;

  const values = useMemo(() => {
    const stored = Object.fromEntries(
      SOCIAL_PLATFORM_DEFS.map((platform) => [
        platform.key,
        networks.find((network) => network.platform === platform.key)?.page_id ?? "",
      ]),
    ) as Record<SocialPlatformKey, string>;
    return hasEdits ? { ...stored, ...edits.values } : stored;
  }, [edits.values, hasEdits, networks]);

  const primary: SocialPlatformKey | "" = useMemo(() => {
    if (hasEdits && edits.primary !== null) return edits.primary;
    return (
      (networks.find((network) => network.is_primary)?.platform as SocialPlatformKey | undefined) ??
      ""
    );
  }, [edits.primary, hasEdits, networks]);

  const patch = useCallback(
    (next: Partial<Omit<SocialEdits, "storeId">>) => {
      setSaveError(null);
      setEdits((current) =>
        current.storeId === storeId
          ? { ...current, ...next, values: { ...current.values, ...next.values } }
          : { storeId, values: next.values ?? {}, primary: next.primary ?? null },
      );
    },
    [storeId],
  );

  const rows = useMemo(
    () =>
      SOCIAL_PLATFORM_DEFS.map((platform) => {
        const value = values[platform.key] ?? "";
        const isFilled = Boolean(normalizeHandle(value));
        return {
          key: platform.key,
          label: platform.labelKey,
          placeholder: platform.placeholderKey,
          helper: platform.helperKey,
          icon: platform.icon,
          brandColor: platform.brandColor,
          value,
          isPrimary: primary === platform.key,
          isFilled,
          onChange: (nextValue: string) => {
            const filled = Boolean(normalizeHandle(nextValue));
            let nextPrimary: SocialPlatformKey | "" | undefined;
            if (!primary && filled) nextPrimary = platform.key;
            if (primary === platform.key && !filled) {
              nextPrimary =
                SOCIAL_PLATFORM_DEFS.find(
                  (item) => item.key !== platform.key && normalizeHandle(values[item.key] ?? ""),
                )?.key ?? "";
            }
            patch({
              values: { [platform.key]: nextValue },
              ...(nextPrimary !== undefined ? { primary: nextPrimary } : {}),
            });
          },
          onSetPrimary: () => {
            if (isFilled) patch({ primary: platform.key });
          },
        };
      }),
    [patch, primary, values],
  );

  const onSubmit = useCallback(async () => {
    if (storeId === null) return;
    setSaveError(null);
    setIsSubmitting(true);
    try {
      // Removals first so the "one primary channel" rule never sees a stale primary.
      const removals = SOCIAL_PLATFORM_DEFS.flatMap((platform) => {
        const existing = networks.find((network) => network.platform === platform.key);
        return existing && !normalizeHandle(values[platform.key] ?? "")
          ? [deleteSocial({ id: existing.id })]
          : [];
      });
      await Promise.all(removals);

      const upserts = SOCIAL_PLATFORM_DEFS.flatMap((platform): Promise<unknown>[] => {
        const handle = normalizeHandle(values[platform.key] ?? "");
        if (!handle) return [];
        const existing = networks.find((network) => network.platform === platform.key);
        const isPrimary = primary === platform.key;
        if (existing) {
          if (existing.page_id === handle && existing.is_primary === isPrimary) return [];
          return [
            updateSocial({ id: existing.id, data: { page_id: handle, is_primary: isPrimary } }),
          ];
        }
        return [
          createSocial({
            data: {
              page_id: handle,
              type: "channel",
              platform: CreateSocialNetworkDtoPlatform[platform.key],
              store_id: storeId,
              is_primary: isPrimary,
            },
          }),
        ];
      });
      await Promise.all(upserts);

      setEdits(EMPTY_EDITS);
      flash();
    } catch (error) {
      setSaveError(
        getApiErrorMessage(error) ?? "ذخیره شبکه‌های اجتماعی انجام نشد؛ دوباره تلاش کنید",
      );
    } finally {
      setIsSubmitting(false);
      void queryClient.invalidateQueries({ queryKey: getGetStoreSocialNetworksQueryKey(storeId) });
    }
  }, [
    createSocial,
    deleteSocial,
    flash,
    networks,
    primary,
    queryClient,
    storeId,
    updateSocial,
    values,
  ]);

  return {
    rows,
    onSubmit: () => void onSubmit(),
    isLoading,
    isSubmitting,
    saved,
    saveError,
  };
}

export type SettingsSocialsState = ReturnType<typeof useSettingsSocials>;
